import { useCallback, useEffect, useMemo, useState } from "react";

import { Table } from "@similarweb/pro-logic-components";

import { useSubtitleFilters } from "app/custom-hooks/useSubtitleFilters";
import { translate } from "app/filters/i18nFilter";
import DurationService from "app/services/DurationService";

import { useAdvertiserActivityDataAndParams } from "../../common/hooks/useAdvertiserActivityDataAndParams";
import { WidgetBoxWithMarginBottom } from "../../common/StyledComponents";
import {
    calculateApiPage,
    FIRST_PAGE,
    getNoDataObject,
    PAGE_SIZE_LARGE,
    PAGE_SIZE_SMALL,
    slicePaginatedData,
} from "../../common/utils/utils";

import { getColumns, renderExpandedRow } from "./SearchCampaignsTableColumns";
import type { ITransformedSearchCampaignData } from "./types";
import { useSearchCampaignsTableRows } from "./useSearchCampaignsTableRows";

export const SEARCH_CAMPAIGNS_TABLE_ID = "search-campaigns-table";

export const SearchCampaignsTable = () => {
    const params = useAdvertiserActivityDataAndParams();
    const subtitleFilters = useSubtitleFilters("date", "country", "webSource");

    const [currentPage, setCurrentPage] = useState(FIRST_PAGE);
    const [expandedRowKey, setExpandedRowKey] = useState<string[]>([]);

    const apiPage = useMemo(() => calculateApiPage(currentPage), [currentPage]);

    const { data, isLoading, error } = useSearchCampaignsTableRows(apiPage, PAGE_SIZE_LARGE);

    const isOneMonthDuration = DurationService.isLessThanOrEqualToOneMonths(params.duration);

    // Reset to page 1 when params change (domain, duration, country)
    useEffect(() => {
        setCurrentPage(FIRST_PAGE);
        setExpandedRowKey([]);
    }, [params.key, params.duration, params.country]);

    const toggleExpandRow = useCallback((rowKey: string) => {
        setExpandedRowKey((prevExpandedKeys) => (prevExpandedKeys.includes(rowKey) ? [] : [rowKey]));
    }, []);

    const columns = useMemo(
        () => getColumns(toggleExpandRow, isOneMonthDuration, params),
        [toggleExpandRow, isOneMonthDuration, params],
    );

    const paginatedData = useMemo(() => {
        if (!data?.items || !data.items.length) {
            return [];
        }
        const sliced = slicePaginatedData(data.items, currentPage);
        return sliced ?? [];
    }, [data, currentPage]);

    const noDataObject = getNoDataObject(error);

    return (
        <WidgetBoxWithMarginBottom id={SEARCH_CAMPAIGNS_TABLE_ID}>
            <Table<ITransformedSearchCampaignData>
                key={isOneMonthDuration ? "single-month" : "multi-month"}
                theme="white"
                bordersConfig={{ body: false, header: false }}
                columnConfig={columns}
                data={paginatedData}
                isLoading={isLoading}
                loaderRowCount={5}
                noData={noDataObject}
                localStorageKey="AdIntelligenceAdvertiserSearchCampaigns"
                pagination={{
                    pageSize: PAGE_SIZE_SMALL,
                    total: data?.totalCount ?? 0,
                    current: currentPage,
                    onChange: (_page, _pageSize, newPage) => setCurrentPage(newPage),
                }}
                expandable={
                    !isOneMonthDuration
                        ? {
                              expandedRowRender: (record) =>
                                  renderExpandedRow(record, () => toggleExpandRow(record.key)),
                              expandIcon: () => null,
                              expandedRowKeys: expandedRowKey,
                              onExpand: (_, record: ITransformedSearchCampaignData) => {
                                  toggleExpandRow(record.key);
                              },
                              expandIconColumnIndex: -1,
                              expandedRowClassName: (record: ITransformedSearchCampaignData) =>
                                  expandedRowKey.length > 0 && expandedRowKey[0] === record.key
                                      ? "expanded-row-active"
                                      : "",
                          }
                        : undefined
                }
            >
                <Table.TableTop
                    borderBottom
                    withoutRowBorders
                >
                    <Table.TableTop.Title
                        title={translate("ad.intelligence.advertiser.search.campaigns.table.title")}
                        tooltip={translate("ad.intelligence.advertiser.search.campaigns.table.title.tooltip")}
                        filters={subtitleFilters}
                    />
                </Table.TableTop>
            </Table>
        </WidgetBoxWithMarginBottom>
    );
};
